import { useEffect, useMemo } from "react";
import { HTTP_STATUSES } from "@miplatform/constants";
import { IBidRequirement, RequirementType } from "@miplatform/types";
import { useBids, useMaterials } from "@mibuildweb/hooks";
import { useCreateBidContext } from "../CreateBidContext";

export const useSelectRequirementsViewModel = () => {
  const { form } = useCreateBidContext();
  const { watch, setValue, getValues } = form;
  const { requirementIds = [], id: bidId } = watch();

  const { isLoading, availableRequirements, getAvailableRequirements } =
    useBids();
  const { materials, setMaterials, getMaterials } = useMaterials();

  const requirementMaterialsIds = useMemo(() => {
    const ids = availableRequirements.flatMap((a) =>
      a.materials.map((m) => m.materialId)
    );
    return [...new Set(ids)].filter(Boolean);
  }, [availableRequirements]);

  const getRequirementMaterials = async () => {
    const response = await getMaterials({
      filters: {
        id: { in: { type: "guid", value: requirementMaterialsIds } },
      },
    });
    if (response.status === HTTP_STATUSES.OK) {
      setMaterials(response.data);
    }
  };

  useEffect(() => {
    if (!bidId) return;

    const pruneStaleRequirements = async () => {
      const response = await getAvailableRequirements(bidId);
      if (response.status !== HTTP_STATUSES.OK) return;
      const eligibleIds = new Set(
        response.data.map((r: IBidRequirement) => r.id)
      );
      const currentIds = getValues("requirementIds") ?? [];
      const prunedIds = currentIds.filter((id) => eligibleIds.has(id));
      if (prunedIds.length !== currentIds.length) {
        setValue("requirementIds", prunedIds, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    };

    pruneStaleRequirements();
  }, [bidId]);

  const groupByType = (requirements: IBidRequirement[]) =>
    requirements.reduce<Record<RequirementType, IBidRequirement[]>>(
      (groups, requirement) => {
        groups[requirement.type] = [
          ...(groups[requirement.type] ?? []),
          requirement,
        ];
        return groups;
      },
      { Inspection: [], Community: [], Builder: [] }
    );

  const selectableRequirements = availableRequirements.filter(
    (requirement) => !requirementIds.includes(requirement.id)
  );

  const groupedAvailableRequirements = groupByType(selectableRequirements);

  const selectedRequirements = requirementIds
    .map((id) => availableRequirements.find((r) => r.id === id))
    .filter((r): r is IBidRequirement => Boolean(r));

  const groupedSelectedRequirements = groupByType(selectedRequirements);

  const handleAddRequirement = (id: string) => {
    if (requirementIds.includes(id)) return;
    setValue("requirementIds", [...requirementIds, id], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleRemoveRequirement = (id: string) => {
    setValue(
      "requirementIds",
      requirementIds.filter((existingId) => existingId !== id),
      { shouldDirty: true, shouldValidate: true }
    );
  };

  return {
    isLoading,
    hasAnyRequirements: availableRequirements.length > 0,
    groupedAvailableRequirements,
    groupedSelectedRequirements,
    materials,
    getRequirementMaterials,
    handleAddRequirement,
    handleRemoveRequirement,
  };
};
